(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  document.documentElement.classList.remove('no-js');

  // E-RACER™ must never break across lines: swap its hyphen for a
  // non-breaking hyphen in every text node (covers rich text, reviews, etc).
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const eracerNodes = [];
  while (walker.nextNode()) {
    if (walker.currentNode.data.includes('E-RACER')) eracerNodes.push(walker.currentNode);
  }
  eracerNodes.forEach((node) => {
    node.data = node.data.replace(/E-RACER/g, 'E‑RACER');
  });

  const header = qs('.header-main');
  if (header) {
    const announcement = qs('.announcement');
    let lastScrollY = window.scrollY;
    let ticking = false;
    const revealOffset = 120;
    const deltaThreshold = 8;

    const syncHeader = () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      const delta = currentScrollY - lastScrollY;
      const announcementOffset = announcement ? Math.max(announcement.offsetHeight - currentScrollY, 0) : 0;
      const headerTop = announcementOffset;
      const headerHeight = header.offsetHeight;
      const nearTop = currentScrollY < revealOffset;
      const scrollingDown = delta > deltaThreshold;
      const scrollingUp = delta < -deltaThreshold;

      document.documentElement.style.setProperty('--header-float-top', `${headerTop}px`);
      document.documentElement.style.setProperty('--mobile-drawer-top', `${headerTop + headerHeight}px`);
      document.body.classList.toggle('header-floating', !nearTop);

      if (nearTop || document.body.classList.contains('menu-open')) {
        document.body.classList.remove('header-hidden');
      } else if (scrollingDown) {
        document.body.classList.add('header-hidden');
      } else if (scrollingUp) {
        document.body.classList.remove('header-hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(syncHeader);
    }, { passive: true });
    window.addEventListener('resize', syncHeader);

    syncHeader();
  }

  let activeMenuToggle = null;
  const setDrawerState = (open, toggle = qs('[data-menu-toggle]')) => {
    const drawer = qs('#MobileMenu');
    if (!drawer) return;

    toggle?.setAttribute('aria-expanded', String(open));
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('menu-open', open);

    if (open) {
      activeMenuToggle = toggle;
      window.setTimeout(() => qs('a, button', drawer)?.focus({ preventScroll: true }), 0);
      return;
    }

    if (drawer.contains(document.activeElement)) {
      activeMenuToggle?.focus({ preventScroll: true });
    }
    activeMenuToggle = null;
  };

  qsa('[data-menu-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      setDrawerState(button.getAttribute('aria-expanded') !== 'true', button);
    });
  });

  qsa('[data-drawer-close]').forEach((button) => {
    button.addEventListener('click', () => {
      setDrawerState(false);
    });
  });

  qsa('#MobileMenu a').forEach((link) => {
    link.addEventListener('click', () => setDrawerState(false));
  });

  const desktopMenuQuery = window.matchMedia('(min-width: 1301px)');
  const closeDrawerOnDesktop = () => {
    if (desktopMenuQuery.matches && document.body.classList.contains('menu-open')) {
      setDrawerState(false);
    }
  };

  closeDrawerOnDesktop();
  if (typeof desktopMenuQuery.addEventListener === 'function') {
    desktopMenuQuery.addEventListener('change', closeDrawerOnDesktop);
  } else {
    desktopMenuQuery.addListener(closeDrawerOnDesktop);
  }

  document.addEventListener('click', (event) => {
    if (!document.body.classList.contains('menu-open')) return;
    const drawer = qs('#MobileMenu');
    if (!drawer) return;
    if (drawer.contains(event.target) || event.target.closest('[data-menu-toggle]')) return;
    setDrawerState(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('menu-open')) {
      setDrawerState(false);
    }
  });

  qsa('[data-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = qs(button.dataset.qty, button.closest('form') || document);
      if (!input) return;
      const current = Number(input.value || 1);
      const next = button.dataset.qtyAction === 'plus' ? current + 1 : Math.max(1, current - 1);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  qsa('[data-media-target]').forEach((button) => {
    button.addEventListener('click', () => {
      const gallery = button.closest('[data-product-gallery]');
      if (!gallery) return;
      const mediaId = button.dataset.mediaTarget;
      qsa('[data-product-media]', gallery).forEach((item) => item.classList.toggle('is-active', item.dataset.productMedia === mediaId));
      qsa('[data-media-target]', gallery).forEach((thumb) => {
        const active = thumb === button;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
      button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  });

  qsa('[data-gallery-scroll]').forEach((button) => {
    const thumbs = button.closest('[data-product-gallery]')?.querySelector('[data-gallery-thumbs]');
    const previous = button.dataset.galleryScroll === 'prev';
    const updatePreviousState = () => {
      if (!previous || !thumbs) return;
      button.classList.toggle('is-visible', thumbs.scrollTop > 4);
    };
    updatePreviousState();
    thumbs?.addEventListener('scroll', updatePreviousState, { passive: true });
    button.addEventListener('click', () => {
      if (!thumbs) return;
      const distance = thumbs.querySelector('.product-gallery__thumb')?.getBoundingClientRect().height || 88;
      thumbs.scrollBy({ top: (button.dataset.galleryScroll === 'next' ? 1 : -1) * (distance + 10) * 3, behavior: 'smooth' });
      window.setTimeout(updatePreviousState, 350);
    });
  });

  qsa('[data-tabs]').forEach((tabs) => {
    const buttons = qsa('[data-tab-button]', tabs);
    const panels = qsa('[data-tab-panel]', tabs);
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.dataset.tabButton;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
        });
        panels.forEach((panel) => {
          const active = panel.dataset.tabPanel === target;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
      });
    });
  });

  const footerAccordions = qsa('.footer-accordion');
  const syncFooterAccordions = () => {
    const mobileFooter = window.matchMedia('(max-width: 860px)').matches;
    footerAccordions.forEach((accordion) => {
      accordion.open = !mobileFooter;
    });
  };
  if (footerAccordions.length) {
    syncFooterAccordions();
    window.addEventListener('resize', syncFooterAccordions);
  }

  qsa('[data-slider]').forEach((slider) => {
    const track = qs('[data-slider-track]', slider);
    const prev = qs('[data-slider-prev]', slider);
    const next = qs('[data-slider-next]', slider);
    if (!track) return;
    const step = () => Math.max(track.clientWidth * 0.85, 280);
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  qsa('[data-marquee]').forEach((track) => {
    track.classList.add('is-interactive');

    let dragging = false;
    let pointerStartX = 0;
    let scrollStartX = 0;
    let resumeAt = 0;

    const loopWidth = () => {
      const innerTrack = qs('.reviews-marquee__track', track);
      return innerTrack ? innerTrack.scrollWidth / 2 : track.scrollWidth / 2;
    };

    const normalizeScroll = () => {
      const width = loopWidth();
      if (width <= 0) return;
      if (track.scrollLeft >= width) track.scrollLeft -= width;
      if (track.scrollLeft <= 0) track.scrollLeft += width;
    };

    track.addEventListener('pointerdown', (event) => {
      dragging = true;
      pointerStartX = event.clientX;
      scrollStartX = track.scrollLeft;
      resumeAt = performance.now() + 900;
      track.classList.add('is-dragging');
      track.setPointerCapture?.(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      event.preventDefault();
      track.scrollLeft = scrollStartX - (event.clientX - pointerStartX);
      normalizeScroll();
    });

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      resumeAt = performance.now() + 900;
      track.classList.remove('is-dragging');
      track.releasePointerCapture?.(event.pointerId);
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('scroll', normalizeScroll, { passive: true });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let lastFrame = performance.now();
    const speed = 28;
    const autoScroll = (now) => {
      const elapsed = Math.min(now - lastFrame, 64);
      lastFrame = now;
      if (!dragging && !document.hidden && now > resumeAt) {
        track.scrollLeft += (speed * elapsed) / 1000;
        normalizeScroll();
      }
      window.requestAnimationFrame(autoScroll);
    };

    window.requestAnimationFrame(autoScroll);
  });

  // Non-marquee review rails can still auto-advance card-by-card.
  qsa('.athletes-hscroll, .athletes-review-grid').forEach((track) => {
    track.classList.add('is-interactive');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const step = () => Math.max(track.clientWidth * 0.8, 280);
    const move = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 8) return;
      if (track.scrollLeft >= maxScroll - 8) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: step(), behavior: 'smooth' });
    };

    window.setInterval(() => {
      if (!document.hidden) move();
    }, 4000);
  });

  const revealTargets = qsa([
    '.hero__copy',
    '.hero__media',
    '.benefit-card',
    '.step-card',
    '.feature-media',
    '.feature-content',
    '.feature-list li',
    '.review-card',
    '.product-gallery',
    '.product-info',
    '.product-proof-strip__item',
    '.team-supply-card',
    '.product-visual-story',
    '.tabs__panel',
    '.ingredient-card',
    '.athlete-band__grid',
    '.athlete-quote',
    '.athletes-hero',
    '.athletes-hscroll .review-card',
    '.athletes-review-display',
    '.athletes-review-display__header',
    '.review-form-card',
    '.faq-intro',
    '.accordion-item',
    '.faq-support-card',
    '.contact-panel',
    '.form-panel',
    '.contact-fast-card',
    '.directions-video__copy',
    '.directions-video__media'
  ].join(','));

  if (revealTargets.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealTargets.forEach((item) => item.classList.add('motion-reveal'));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

    revealTargets.forEach((item) => observer.observe(item));
  } else {
    revealTargets.forEach((item) => item.classList.add('is-visible'));
  }

  qsa('[data-copy-link]').forEach((button) => {
    button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(button.dataset.copyLink || window.location.href);
        button.classList.add('is-copied');
        setTimeout(() => button.classList.remove('is-copied'), 1500);
      } catch (error) {
        window.prompt('Copy this link', button.dataset.copyLink || window.location.href);
      }
    });
  });
})();
